/**
 * useArtifactSave — shared hook for saving edited artifact / code-block content
 * back to the chat answer document.
 *
 * Handles the common flow used by both the standard chat `<CodeBlock>` and
 * Nebula `<NebulaCanvas>`:
 *   1. POST `/spark/update_answer_content` with the rebuilt full answer string
 *   2. Patch the TanStack Query cache so SingleMessage reflects the change
 *      immediately without a reload
 *   3. Manage `saveStatus` (null → 'saving' → 'saved' / 'error') with auto-clear
 *
 * The caller supplies `buildUpdatedContent(editedCode)` which produces the full
 * answer string with the edited block substituted in. This is the only piece
 * that differs between surfaces — Nebula has one fenced artifact per answer
 * (with several possible tag aliases), CodeBlock has N blocks identified by
 * (langTag, blockIndex).
 */

import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "../api";
import { updateChatMessagesCache } from "../api/queryHooks";
import { useSparkContext } from "../context/SparkContext";
import QUERYKEYS from "../api/queryKeys";

const SAVED_CLEAR_MS = 2000;
const ERROR_CLEAR_MS = 3000;

/**
 * @param {object}   args
 * @param {string?}  args.answerId
 * @param {string?}  args.chatOwnerId
 * @param {(editedCode: string) => string | null} args.buildUpdatedContent
 *        — must return the full updated answer content, or null to abort
 * @returns {{ saveStatus, save, reset }}
 */
export default function useArtifactSave({ answerId, chatOwnerId, buildUpdatedContent }) {
  const { chat_id, space_id } = useSparkContext();
  const queryClient = useQueryClient();
  const [saveStatus, setSaveStatus] = useState(null);
  const clearTimerRef = useRef(null);

  const scheduleClear = useCallback((ms) => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => setSaveStatus(null), ms);
  }, []);

  /**
   * Persist the edited code. Returns the updated content string on success,
   * or null on failure / missing prerequisites — letting the caller sync any
   * local "savedBase" state to the value that was actually persisted.
   *
   * @param {string} editedCode
   * @param {"manual_edit"|"inline_ai_edit"} [versionTrigger="manual_edit"]
   * @param {string|null} [versionPrompt=null] Raw user prompt that produced this
   *   version. Persisted on the version doc so the history panel can show intent
   *   and offer click-to-replay. Only meaningful for inline AI edits.
   */
  const save = useCallback(
    async (editedCode, versionTrigger = "manual_edit", versionPrompt = null) => {
      if (!answerId || !chat_id || !chatOwnerId) return null;
      const updated_content = buildUpdatedContent(editedCode);
      if (!updated_content) return null;
      setSaveStatus("saving");
      try {
        await api({
          method: "POST",
          url: "/spark/update_answer_content",
          data: {
            user_id: chatOwnerId,
            owner_id: chatOwnerId,
            space_id,
            chat_id,
            answer_id: answerId,
            updated_content,
            version_trigger: versionTrigger,
            ...(versionPrompt ? { version_prompt: versionPrompt } : {}),
          },
        });
        updateChatMessagesCache(queryClient, chat_id, space_id, (messages) =>
          messages.map((msg) => ({
            ...msg,
            answers: (msg.answers || []).map((a) =>
              a.id === answerId ? { ...a, content: updated_content } : a
            ),
          }))
        );
        queryClient.invalidateQueries({ queryKey: [QUERYKEYS.ARTIFACT_VERSIONS, chat_id, answerId] });
        setSaveStatus("saved");
        scheduleClear(SAVED_CLEAR_MS);
        return updated_content;
      } catch {
        setSaveStatus("error");
        scheduleClear(ERROR_CLEAR_MS);
        return null;
      }
    },
    [answerId, chat_id, chatOwnerId, space_id, buildUpdatedContent, queryClient, scheduleClear]
  );

  const reset = useCallback(() => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    setSaveStatus(null);
  }, []);

  return { saveStatus, save, reset };
}
